/**
 * Facility Service
 * 
 * This service layer provides an abstraction over facility-related API calls.
 * It handles all facility CRUD operations, filtering, and status management.
 * 
 * @module FacilityService
 */

import { getFacilities, getFacilityById, createFacility, editFacility } from '../utils/apiClient';

/**
 * Fetches facilities based on provided API parameters
 * 
 * @param {Object} apiParams - Already converted API parameters
 * @param {boolean} [apiParams.include_inactive] - Whether to include inactive facilities
 * @param {string} [apiParams.location] - Location filter (future use)
 * @param {string} [apiParams.plan_type] - Plans filter (future use)
 * @returns {Promise<Array>} Array of facility objects
 * 
 * @example
 * const facilities = await FacilityService.getAll({ include_inactive: true });
 */
export const getAll = async (apiParams = {}) => {
  try {
    const facilities = await getFacilities(apiParams);
    return facilities || [];
  } catch (error) {
    console.error('Error in FacilityService.getAll:', error);
    throw new Error('Failed to fetch facilities. Please try again.');
  }
};

/**
 * Gets a facility by ID with departments included
 * 
 * @param {string} facilityId - ID of the facility to fetch
 * @returns {Promise<Object>} Facility details with departments
 * 
 * @example
 * const facility = await FacilityService.getById('facility-id');
 */
export const getById = async (facilityId) => {
  try {
    const response = await getFacilityById(facilityId);
    
    console.log('🔍 Raw API response in service:', response);
    
    // Handle the actual API response structure: { statusCode: "SUCCESS", response: {...facility data...}, message: "..." }
    if (response.statusCode === 'SUCCESS' && response.response) {
      const facilityData = response.response || {};
      const filteredDepartments = Array.isArray(facilityData.departments)
        ? facilityData.departments.filter(dept => dept?.isActive !== false)
        : facilityData.departments;

      const facilityWithActiveDepartments = {
        ...facilityData,
        departments: filteredDepartments
      };

      console.log('✅ Facility data with departments:', facilityWithActiveDepartments);
      return {
        success: true,
        data: facilityWithActiveDepartments  // The actual facility data is in response.response
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to fetch facility details'
    };
  } catch (error) {
    console.error('Error in FacilityService.getById:', error);
    throw new Error('Failed to fetch facility details. Please try again.');
  }
};

/**
 * Creates a new facility
 * 
 * @param {Object} facilityData - Facility data to create
 * @param {string} facilityData.facilityName - Name of the facility
 * @param {Object} facilityData.location - Location information
 * @param {string} facilityData.phoneNumber - Contact phone number
 * @param {string} facilityData.groupNPI - Group NPI number
 * @param {Array} facilityData.inNetworkInsurancePlans - Insurance plans
 * @returns {Promise<Object>} Created facility response
 * 
 * @example
 * const result = await FacilityService.create(facilityData);
 */
export const create = async (facilityData) => {
  try {
    const response = await createFacility(facilityData);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        message: response.message || 'Facility created successfully',
        data: response.data
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to create facility'
    };
  } catch (error) {
    console.error('Error in FacilityService.create:', error);
    
    // Handle specific error cases
    if (error?.response?.status === 409 || error?.response?.data?.statusCode === 'CONFLICT') {
      const duplicate = error.response.data?.response?.duplicate_facility;
      const npi = duplicate?.groupNPI ? `Group NPI ${duplicate.groupNPI}` : 'This NPI';
      const message = `${npi} already exists for an active facility at the same location. Please use a different NPI or change the location.`;
      
      return {
        success: false,
        message,
        errorType: 'CONFLICT'
      };
    }
    
    throw new Error('Failed to create facility. Please try again.');
  }
};

/**
 * Updates an existing facility
 * 
 * @param {string} facilityId - ID of the facility to update
 * @param {Object} facilityData - Updated facility data
 * @returns {Promise<Object>} Update response
 * 
 * @example
 * const result = await FacilityService.update('facility-id', updatedData);
 */
export const update = async (facilityId, facilityData) => {
  try {
    const response = await editFacility(facilityId, facilityData);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        message: response.message || 'Facility updated successfully',
        data: response.data
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to update facility'
    };
  } catch (error) {
    console.error('Error in FacilityService.update:', error);
    throw new Error('Failed to update facility. Please try again.');
  }
};

/**
 * Toggles facility status (activate/deactivate)
 * 
 * @param {string} facilityId - ID of the facility
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Status toggle response
 * 
 * @example
 * const result = await FacilityService.toggleStatus('facility-id', false);
 */
export const toggleStatus = async (facilityId, isActive) => {
  try {
    const response = await editFacility(facilityId, { isActive });
    
    if (response.statusCode === 'SUCCESS') {
      const actionText = isActive ? 'activated' : 'deactivated';
      return {
        success: true,
        message: `Facility ${actionText} successfully`
      };
    }
    
    return {
      success: false,
      message: `Failed to ${isActive ? 'activate' : 'deactivate'} facility`
    };
  } catch (error) {
    console.error('Error in FacilityService.toggleStatus:', error);
    const actionText = isActive ? 'activate' : 'deactivate';
    throw new Error(`Failed to ${actionText} facility. Please try again.`);
  }
};



/**
 * Validates facility data before submission
 * 
 * @param {Object} facilityData - Facility data to validate
 * @returns {Object} Validation result
 * 
 * @example
 * const validation = FacilityService.validateData(facilityData);
 * if (!validation.isValid) console.log(validation.errors);
 */
export const validateData = (facilityData) => {
  const errors = [];
  
  if (!facilityData.facilityName?.trim()) {
    errors.push('Facility name is required');
  }
  
  if (!facilityData.phoneNumber?.trim()) {
    errors.push('Phone number is required');
  }
  
  if (!facilityData.groupNPI?.trim()) {
    errors.push('Group NPI is required');
  }
  
  if (!facilityData.location?.streetAddress?.trim()) {
    errors.push('Street address is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Export as default object for easier importing
const FacilityService = {
  getAll,
  getById,
  create,
  update,
  toggleStatus,
  validateData
};

export default FacilityService;
